import { useMemo, useState } from "react";
import PropTypes from "prop-types";

import {
    Box,
    Tab,
    Tabs,
    IconButton,
    Tooltip,
    Stack,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";

import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import HtmlOutlinedIcon from "@mui/icons-material/HtmlOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import JsonViewer from "./JsonViewer";
import HtmlViewer from "./HtmlViewer";
import TextViewer from "./TextViewer";

import {
    cleanParsePage, cleanSplitPage, downloadBlobFile, cleanTranscribePage, handleDownloadJson,
} from "../../helpers/editorHelper";

import { HEADER_HEIGHT } from "../../constants/layout";
import { JOB_TYPE } from "../../constants/constants";
import { JOB_STATUS } from "../../constants/status";
import {downloadFile} from "../../api/fileRoutes";

export default function DocumentInspector({
                                              job,
                                              currentPage,
                                              hoveredChunkId,
                                              setHoveredChunkId,
                                              leftWidth
                                          }) {
    const [tab, setTab] = useState(0);
    const [exportLoading, setExportLoading] = useState(null);
    const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);

    const page = job?.file?.pages?.[currentPage - 1];

    const rawPages = useMemo(() => {
        return job?.file?.pages ?? [];
    }, [job?.file?.pages]);

    const isSplit = job?.type === JOB_TYPE.SPLIT;
    const isTranscribe = job?.type === JOB_TYPE.TRANSCRIBE;

    const activePage = useMemo(() => {
        return rawPages[currentPage - 1];
    }, [rawPages, currentPage]);

    const pageJsonData = useMemo(() => {
        if (!page) {
            return null;
        }

        if (isTranscribe) return cleanTranscribePage(page);
        return isSplit
            ? cleanSplitPage(page)
            : cleanParsePage(page);

    }, [isSplit, page]);

    const tabs = useMemo(() => {
        if (isSplit) return ["JSON"];
        if (isTranscribe) return ["Text", "JSON"];
        return ["HTML", "JSON"];
    }, [isSplit, isTranscribe]);

    const activeTab = tab;

    const handleOpenDownloadMenu = (event) => {
        setDownloadAnchorEl(event.currentTarget);
    };

    const handleCloseDownloadMenu = () => {
        setDownloadAnchorEl(null);
    };

    const handleDownloadFile = async (format) => {
        try {
            setExportLoading(format);

            const response = await downloadFile(job?.file?.id, format);

            const mimeTypes = {
                docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                pdf: "application/pdf",
                html: "application/zip",
            };

            const extensions = {
                docx: "docx",
                pdf: "pdf",
                html: "zip",
            };

            const fileName = job?.file?.name
                ?.replace(/[<>:"/\\|?*]/g, "_")
                ?.replace(/\.[^/.]+$/, "");

            downloadBlobFile(
                response.data,
                `${fileName}.${extensions[format]}`,
                mimeTypes[format]
            );

        } catch (error) {
            console.error(`${format} download failed`, error);

        } finally {
            setExportLoading(null);
            handleCloseDownloadMenu();
        }
    };

    const handleJsonDownload = () => {
        handleDownloadJson(job);
        handleCloseDownloadMenu();
    };

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box
                sx={{
                    px: 2,
                    height: HEADER_HEIGHT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #f1f5f9",
                    background: "white",
                }}
            >
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setTab(v)}
                >
                    {tabs.map((label) => (
                        <Tab
                            key={label}
                            label={label}
                        />
                    ))}
                </Tabs>

                <Stack direction="row" spacing={1}>
                    <Tooltip title="Download">
                        <span>
                            <IconButton
                                size="small"
                                disabled={
                                    job?.status !== JOB_STATUS.COMPLETED
                                }
                                onClick={handleOpenDownloadMenu}
                            >
                                <DownloadRoundedIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Menu
                        anchorEl={downloadAnchorEl}
                        open={Boolean(downloadAnchorEl)}
                        onClose={handleCloseDownloadMenu}
                    >
                        <MenuItem onClick={handleJsonDownload}>
                            <ListItemIcon>
                                <DataObjectOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>
                                JSON
                            </ListItemText>
                        </MenuItem>

                        <MenuItem onClick={() => handleDownloadFile("docx")}>
                            <ListItemIcon>
                                <DescriptionOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>
                                Word (.docx)
                            </ListItemText>
                            {exportLoading === "docx" && (
                                <Box sx={{ ml: 2 }}>
                                    <CircularProgress size={16} />
                                </Box>
                            )}
                        </MenuItem>

                        {!isTranscribe && <MenuItem onClick={() => handleDownloadFile("html")}>
                            <ListItemIcon>
                                <HtmlOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>
                                Html
                            </ListItemText>
                            {exportLoading === "html" && (
                                <Box sx={{ ml: 2 }}>
                                    <CircularProgress size={16} />
                                </Box>
                            )}
                        </MenuItem>}

                        {/*!isTranscribe && <MenuItem onClick={() => handleDownloadFile("pdf")}>
                            <ListItemIcon>
                                <PictureAsPdfOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>
                                Searchable PDF
                            </ListItemText>
                            {exportLoading === "pdf" && (
                                <Box sx={{ ml: 2 }}>
                                    <CircularProgress size={16} />
                                </Box>
                            )}
                        </MenuItem>*/}
                    </Menu>
                </Stack>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    overflow: "auto",
                    p: 2,
                }}
            >
                {isSplit ? (
                    <JsonViewer
                        page={pageJsonData}
                    />
                ) : isTranscribe ? (
                    <>
                        {activeTab === 0 && (
                            <TextViewer page={activePage} />
                        )}
                        {activeTab === 1 && (
                            <JsonViewer page={pageJsonData} />
                        )}
                    </>
                ) : (
                    <>
                        {activeTab === 0 && (
                            <HtmlViewer
                                page={activePage}
                                hoveredChunkId={hoveredChunkId}
                                setHoveredChunkId={setHoveredChunkId}
                                leftWidth={leftWidth}
                            />
                        )}

                        {activeTab === 1 && (
                            <JsonViewer
                                page={pageJsonData}
                            />
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}

DocumentInspector.propTypes = {
    job: PropTypes.object,
    currentPage: PropTypes.number,
    hoveredChunkId: PropTypes.string,
    leftWidth: PropTypes.string,
    setHoveredChunkId: PropTypes.func,
};